// Surface model — the "capture vs desk" layer that names *why* a phone and a
// desktop get genuinely different component variants.
//
//   - capture-surface: field data entry — single column, big tap targets,
//     minimal chrome. The phone experience.
//   - desk-surface: office workflows — multi-pane, dense tables, keyboard
//     shortcuts, hover/right-click affordances. The desktop experience.
//
// Surface is binary, derived from the (ternary) device class in viewport.ts so
// there is a single source of truth for "how wide are we":
//
//   phone   → capture
//   tablet  → desk   (the desk primitives are responsive, so a tablet renders a
//                     simplified desk that sits between the two — multi-pane
//                     collapses, dense tables shed non-essential columns)
//   desktop → desk
//
// This drives LAYOUT only — it follows the viewport, not the authenticated
// device_class (a window resize re-surfaces; it never changes capabilities,
// which are auth-bound per /). For the auth-side classifier see
// authDeviceClass in viewport.ts.

import { getDeviceClass, onDeviceClassChange, type DeviceClass } from './viewport';

export type Surface = 'capture' | 'desk';

export function surfaceForDeviceClass(dc: DeviceClass): Surface {
  return dc === 'phone' ? 'capture' : 'desk';
}

export function setRootSurface(): Surface {
  const s = surfaceForDeviceClass(getDeviceClass());
  document.documentElement.setAttribute('data-surface', s);
  return s;
}

type Listener = (s: Surface) => void;

const listeners = new Set<Listener>();
let current: Surface = surfaceForDeviceClass(getDeviceClass());

// Re-derive whenever the device class crosses a breakpoint. Only notify on an
// actual capture↔desk transition: tablet→desktop stays 'desk', so a component
// that only cares about its surface isn't torn down and rebuilt needlessly.
onDeviceClassChange((dc) => {
  const next = surfaceForDeviceClass(dc);
  if (next === current) return;
  current = next;
  document.documentElement.setAttribute('data-surface', next);
  listeners.forEach((fn) => fn(next));
});

export function getSurface(): Surface {
  return current;
}

export function onSurfaceChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
